<script lang="ts">
export interface ColumnSelectionProps<TData extends RowData>
	extends Partial<RowSelectionTableState>,
		RowSelectionOptions<TData> {
	singleSelect?: boolean;
}

export type SelectionMode = "single" | "multiple";

export function toggleSelection(
	selection: RowSelectionState,
	id: string,
	mode: SelectionMode = "multiple"
): RowSelectionState {
	const isSelected = id in selection;
	if (mode === "single") {
		return isSelected ? {} : { [id]: true };
	}

	// Remove the id from selection, if it exist
	const { [id]: _, ...rest } = selection;

	// Return the rest if it was selected or the selection with the id added
	return isSelected ? rest : { ...selection, [id]: true };
}
</script>

<script setup lang="ts" generic="TData extends RowData">
import type {
	Row,
	RowData,
	RowSelectionOptions,
	RowSelectionState,
	RowSelectionTableState,
	Table,
} from "@tanstack/vue-table";
import { watch } from "vue";
import Checkbox from "../checkbox/Checkbox.vue";
import { injectDataCollectionContext } from "../data-collection/composables/data-collection";
import Column from "./Column.vue";
import { useStateModel } from "./state/state-model";

const { state, options } = injectDataCollectionContext();

const props = withDefaults(
	defineProps<{
		selection?: RowSelectionState;
		selectionMode?: SelectionMode;
	}>(),
	{
		selectionMode: "single",
	}
);
const emit = defineEmits<{
	"update:selection": [val: RowSelectionState];
}>();

useStateModel("selection", props, emit, state);

watch(
	() => props.selectionMode,
	(mode) => {
		options.enableMultiRowSelection = mode === "multiple";
	},
	{ immediate: true }
);

function onMultiRowChange(table: Table<unknown>, value: boolean | "indeterminate") {
	if (value === "indeterminate") {
		return;
	}

	table.toggleAllPageRowsSelected(!!value);
}

function onRowChange(table: Table<unknown>, row: Row<unknown>) {
	const isSelected = row.getIsSelected();
	if (props.selectionMode === "single") {
		table.toggleAllRowsSelected(false);
	}
	row.toggleSelected(!isSelected);
}

function globalSelectionState(table: Table<any>): boolean | "indeterminate" {
	if (table.getIsAllPageRowsSelected()) {
		return true;
	}

	return table.getIsSomePageRowsSelected() ? "indeterminate" : false;
}
</script>

<template>
	<Column v-if="selectionMode != null" class="w-12">
		<template #header="{ table }">
			<Checkbox
				v-if="table.options.enableMultiRowSelection"
				size="sm"
				color="neutral"
				:modelValue="globalSelectionState(table)"
				@update:modelValue="(state) => onMultiRowChange(table, state)"
			/>
		</template>
		<template #default="{ table, row }">
			<Checkbox
				:class="selectionMode === 'single' ? 'rounded-full' : ''"
				size="sm"
				color="neutral"
				:modelValue="row.getIsSelected()"
				@update:modelValue="onRowChange(table, row)"
			/>
		</template>
	</Column>
</template>
